import React, {useMemo} from "react"
import styled from "styled-components"
import {H3, Body} from "@irati/ui/src/Typography"
import Spacing from "@irati/ui/src/Spacing"
import Tabs from "@irati/ui/src/Tabs"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {useProductDetailQuery} from "@irati/payrollpay/hooks"
import {RouteComponentProps} from "react-router-dom"
import Spinner from "@irati/ui/src/Loading/Spinner"
import {QueryProductDetailArgs} from "@irati/types"
import {useStoreContext} from "../context/StoreContext"

interface RouteParams {
  id: string
}

interface Props extends RouteComponentProps<RouteParams> {}

const ProductScreen: React.FC<Props> = ({match, location}) => {
  const {schemeType} = useStoreContext()
  const params = new URLSearchParams(location.search)
  const brochureGroupId = params.get("bg") || ""

  const queryVariables: QueryProductDetailArgs = useMemo(
    () => ({
      organisationId: "566-zz-nhs-demo",
      schemeType,
      id: match.params.id,
      brochureGroupId,
      locale: "en-GB"
    }),
    [match.params.id, brochureGroupId, schemeType]
  )

  const {data: productDetail, loading} = useProductDetailQuery(queryVariables, {
    fetchPolicy: "cache-and-network"
  })

  const product = productDetail?.employeeOrganisation?.scheme?.product

  if (loading || !product) {
    return <Spinner />
  }

  return (
    <OuterContainer>
      <FlexContainer>
        <ImageContainer>
          <ProductImage>
            <Image src={product.image || ""} alt={product.name || ""} />
          </ProductImage>
          <ThumbnailContainer>
            {product.productImages?.map((image, index) => (
              <ThumbnailImage key={index} src={image} alt={`${product.name} thumbnail ${index + 1}`} />
            ))}
          </ThumbnailContainer>
        </ImageContainer>

        <ContentContainer>
          <MainHeading>{product.name}</MainHeading>
          <Spacing marginTop={2} hideOnMobile>
            <Content dangerouslySetInnerHTML={{__html: product.description || ""}} />
          </Spacing>
          <Content marginTop={2}>Monthly amount from take home pay:</Content>
          <Spacing marginTop={2}>
            <Content>
              <PriceTag>£{product.price?.toFixed(2) || 0}</PriceTag>
            </Content>
          </Spacing>

          {/* Dropdown */}

          <Spacing marginTop={2} marginBottom={2}>
            <Content>
              Total amount from take home pay: <br />£{product.totalAmount?.toFixed(2) || 0}
            </Content>
          </Spacing>

          <Spacing marginTop={2}>
            <ButtonRouterLink to="/basket" full primary>
              Add to order
            </ButtonRouterLink>
          </Spacing>
        </ContentContainer>
      </FlexContainer>
      <TabContainer>
        <Tabs
          tabs={[
            {
              key: "product_details",
              title: "Product details",
              component: <div dangerouslySetInnerHTML={{__html: product.summary || ""}} />
            },
            {
              key: "specification",
              title: "Specification",
              component: <div dangerouslySetInnerHTML={{__html: product.techSpecs || ""}} />
            }
          ]}
        />
      </TabContainer>
    </OuterContainer>
  )
}

export default ProductScreen

const OuterContainer = styled.div`
  flex-grow: 1;
  width: 100%;
`

const FlexContainer = styled.div`
  display: flex;
  margin: 48px auto;
  flex-direction: column;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    flex-direction: row;
    max-width: 1080px;
  `}
`

const ImageContainer = styled.div`
  margin: auto;
`

const ProductImage = styled.div`
  position: relative;
  height: 248px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
  margin: auto;

  @media screen and (max-width: 1280px) {
    max-width: 100%;
  }

  @media screen and (min-width: 600px) and (max-width: 900px) {
    max-width: 80%;
  }

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    width: 386px;
    height: 334px;
  `}
`

const Image = styled.img`
  max-width: 100%;
  height: 100%;
  object-fit: contain;
`

const ThumbnailContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 48px auto 24px auto;
  column-gap: 12px;
`

const ThumbnailImage = styled.img`
  height: 100px;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    height: 120px;
  `}
`

const ContentContainer = styled.div`
  max-width: 320px;
  margin: 0 auto;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    max-width: 485px;
    padding-left: 20px;
  `}
`

const MainHeading = styled(H3)`
  font-size: 14px;
  line-height: 24px;
  margin-top: 10px;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    font-size: 24px;
    font-weight: bold;
    line-height: 2rem;
  `}
`

const Content = styled(Body)`
  font-size: 12px;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    font-size: 16px;
  `}
`

const PriceTag = styled.span`
  font-size: 24px;
  margin-right: 4px;
  color: ${({theme}) => theme.colors.primary1};
`

const TabContainer = styled.div`
  margin: 0 24px;

  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
  max-width: 1080px;
  margin: 0 auto;

  `}
`
